import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const hoy = format(new Date(), 'yyyy-MM-dd');

const ESTADO_COLORS = {
  borrador: { bg: '#FEF3C7', color: '#92400E', label: 'Borrador' },
  enviado:  { bg: '#D1FAE5', color: '#065F46', label: 'Enviado ✓' },
  recibido: { bg: '#DBEAFE', color: '#1E40AF', label: 'Recibido ✓' },
};

const CAT_INFO = {
  empaques:    { label: 'Empaques',     icon: '🛍️' },
  aseo:        { label: 'Aseo',         icon: '🧹' },
  utensilios:  { label: 'Utensilios',   icon: '🔧' },
  insumos:     { label: 'Insumos',      icon: '📦' },
  ingredientes:{ label: 'Ingredientes', icon: '🧪' },
  papeleria:   { label: 'Papelería',    icon: '📄' },
};

export default function EnviosGeneral() {
  const { usuario } = useAuth();
  const esAdmin = ['gerencia', 'administracion'].includes(usuario?.rol);

  const [tab, setTab]             = useState('nuevo');
  const [bodegas, setBodegas]     = useState([]);
  const [items, setItems]         = useState([]);
  const [bodegaId, setBodegaId]   = useState('');
  const [fecha, setFecha]         = useState(hoy);
  const [detalle, setDetalle]     = useState({});
  const [observacion, setObs]     = useState('');
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [envioDetalle, setEnvioDetalle] = useState(null);
  const [envioEditar, setEnvioEditar]   = useState(null); // envío a corregir
  const [invPDV, setInvPDV]       = useState({ bodegas: [], items: [] });
  const [cargandoInv, setCargandoInv] = useState(false);

  // Cargar bodegas PDV e items enviables
  useEffect(() => {
    Promise.all([
      API.get('/bodegas'),
      API.get('/inventario/items', { params: { tipo: 'semanal' } }),
      API.get('/inventario/items', { params: { tipo: 'diario' } }),
    ]).then(([b, itSem, itDia]) => {
      const pvs = b.data.filter(x => x.tipo === 'punto_venta');
      setBodegas(pvs);
      const todos = [...itSem.data, ...itDia.data];
      const unicos = todos.filter((item, idx, arr) => arr.findIndex(x => x.id === item.id) === idx);
      setItems(unicos);
      if (pvs.length) setBodegaId(String(pvs[0].id));
    }).catch(() => {});
  }, []);

  // Historial
  useEffect(() => {
    if (tab !== 'historial') return;
    API.get('/envios-general', { params: { bodega_id: bodegaId || null } })
      .then(r => setHistorial(r.data))
      .catch(() => {});
  }, [tab, bodegaId]);

  // Inventario PDV
  useEffect(() => {
    if (tab !== 'inventario') return;
    setCargandoInv(true);
    API.get('/envios-general/inventario-pdv')
      .then(r => setInvPDV(r.data))
      .catch(() => {})
      .finally(() => setCargandoInv(false));
  }, [tab]);

  const totalItems = Object.values(detalle).filter(v => parseFloat(v) > 0).length;

  const handleGuardar = async (enviar = false) => {
    if (!bodegaId) { toast.error('Selecciona un punto de venta'); return; }
    if (totalItems === 0) { toast.error('Agrega al menos un ítem'); return; }
    setGuardando(true);
    try {
      const det = items
        .filter(i => parseFloat(detalle[i.id]) > 0)
        .map(i => ({ item_id: i.id, cantidad: parseFloat(detalle[i.id]) }));
      await API.post('/envios-general', { bodega_id: bodegaId, fecha, items: det, observacion, enviar });
      toast.success(enviar ? '✅ Envío registrado y confirmado' : '💾 Borrador guardado');
      setDetalle({});
      setObs('');
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setGuardando(false); }
  };

  // Cargar envío para editar (admin)
  const handleCorregir = async (id) => {
    try {
      const r = await API.get(`/envios-general/${id}`);
      const envio = r.data;
      // Cargar datos en el formulario
      setBodegaId(String(envio.bodega_id || bodegaId));
      setFecha(envio.fecha ? envio.fecha.slice(0, 10) : hoy);
      setObs(envio.observacion || '');
      const det = {};
      (envio.detalle || []).forEach(d => { det[d.item_id] = d.cantidad; });
      setDetalle(det);
      setEnvioEditar(envio);
      setTab('nuevo');
      toast('✏️ Cargando envío para corrección...', { icon: '⚠️' });
    } catch { toast.error('Error cargando envío'); }
  };

  // Guardar corrección (admin fuerza re-envío)
  const handleGuardarCorreccion = async () => {
    if (totalItems === 0) { toast.error('Agrega al menos un ítem'); return; }
    setGuardando(true);
    try {
      const det = items
        .filter(i => parseFloat(detalle[i.id]) > 0)
        .map(i => ({ item_id: i.id, cantidad: parseFloat(detalle[i.id]) }));
      await API.post('/envios-general/corregir', {
        envio_id: envioEditar.id,
        bodega_id: bodegaId,
        fecha,
        items: det,
        observacion,
      });
      toast.success('✅ Envío corregido correctamente');
      setDetalle({});
      setObs('');
      setEnvioEditar(null);
      setTab('historial');
    } catch (e) { toast.error(e.response?.data?.error || 'Error al corregir'); }
    finally { setGuardando(false); }
  };

  const verDetalle = async (id) => {
    try {
      const r = await API.get(`/envios-general/${id}`);
      setEnvioDetalle(r.data);
    } catch { toast.error('Error cargando detalle'); }
  };

  // Agrupar items por categoría
  const porCategoria = {};
  items.forEach(item => {
    const cat = item.categoria || 'otros';
    if (!porCategoria[cat]) porCategoria[cat] = [];
    porCategoria[cat].push(item);
  });

  return (
    <div style={{ fontFamily: "'Segoe UI',sans-serif", maxWidth: 960, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>
          📦 Envíos de Productos
        </h1>
        <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>
          Registro de despachos de insumos y empaques desde producción a puntos de venta
        </p>
      </div>

      {/* Controles superiores */}
      <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 18, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div style={st.fieldLabel}>Punto de venta</div>
          <select value={bodegaId} onChange={e => setBodegaId(e.target.value)} style={st.select}>
            {bodegas.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </div>
        <div>
          <div style={st.fieldLabel}>Fecha envío</div>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} style={st.input} />
        </div>

        {/* Tabs */}
        <div style={{ marginLeft: 'auto', display: 'flex', background: '#F3F4F6', borderRadius: 8, padding: 2 }}>
          {[
            { id: 'nuevo',      label: '📦 Nuevo envío' },
            { id: 'historial',  label: '📋 Historial' },
            { id: 'inventario', label: '🔍 Inventario PDV' },
          ].map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setEnvioEditar(null); }} style={{
              padding: '6px 14px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13,
              fontWeight: tab === t.id ? 700 : 500,
              background: tab === t.id ? 'white' : 'transparent',
              color: tab === t.id ? '#111827' : '#6B7280',
              boxShadow: tab === t.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── TAB: HISTORIAL ── */}
      {tab === 'historial' && (
        envioDetalle ? (
          <DetalleEnvio envio={envioDetalle} onVolver={() => setEnvioDetalle(null)} />
        ) : (
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', fontWeight: 800, fontSize: 15 }}>
              📋 Historial de envíos
            </div>
            {historial.length === 0 ? (
              <div style={{ padding: 36, textAlign: 'center', color: '#9CA3AF' }}>Sin envíos registrados</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F9FAFB' }}>
                    {['Fecha', 'PDV', 'Items', 'Estado', 'Registrado por', ''].map(h => (
                      <th key={h} style={st.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historial.map((h, i) => {
                    const ec = ESTADO_COLORS[h.estado] || ESTADO_COLORS.borrador;
                    return (
                      <tr key={h.id} style={{ borderTop: '1px solid #F3F4F6', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                        <td style={st.td}><strong>{h.fecha ? format(new Date(h.fecha.slice(0,10) + 'T12:00'), "d MMM yyyy", { locale: es }) : '—'}</strong></td>
                        <td style={st.td}>{h.bodega_nombre}</td>
                        <td style={{ ...st.td, fontFamily: 'monospace', fontWeight: 800, fontSize: 15 }}>{h.total_items}</td>
                        <td style={st.td}>
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: ec.bg, color: ec.color, fontWeight: 700 }}>
                            {ec.label}
                          </span>
                        </td>
                        <td style={{ ...st.td, color: '#6B7280' }}>{h.registrado_por_nombre || '—'}</td>
                        <td style={{ ...st.td, display: 'flex', gap: 6 }}>
                          <button onClick={() => verDetalle(h.id)} style={st.btnVer}>Ver</button>
                          {esAdmin && (
                            <button onClick={() => handleCorregir(h.id)} style={{ ...st.btnVer, borderColor: '#F59E0B', color: '#92400E', background: '#FFFBEB' }}>
                              ✏️ Corregir
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )
      )}

      {/* ── TAB: INVENTARIO PDV ── */}
      {tab === 'inventario' && (
        <TabInventarioPDV data={invPDV} cargando={cargandoInv} />
      )}

      {/* ── TAB: NUEVO ENVÍO ── */}
      {tab === 'nuevo' && (
        <div>
          {/* Banner corrección */}
          {envioEditar && (
            <div style={{ background: '#FEF3C7', border: '2px solid #F59E0B', borderRadius: 12, padding: '12px 18px', marginBottom: 16, fontSize: 13, color: '#92400E', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>✏️ Corrigiendo envío #{envioEditar.id} — {envioEditar.bodega_nombre} ({envioEditar.fecha?.slice(0,10)})</span>
              <button onClick={() => { setEnvioEditar(null); setDetalle({}); setObs(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#92400E' }}>✕</button>
            </div>
          )}

          {/* Resumen */}
          {totalItems > 0 && (
            <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#1D4ED8', fontWeight: 600 }}>
              📦 {totalItems} tipo{totalItems > 1 ? 's' : ''} de item{totalItems > 1 ? 's' : ''} seleccionado{totalItems > 1 ? 's' : ''}
            </div>
          )}

          {/* Items por categoría */}
          {Object.entries(porCategoria).map(([cat, catItems]) => {
            const catInfo = CAT_INFO[cat] || { label: cat, icon: '📋' };
            return (
              <div key={cat} style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 14 }}>
                <div style={{ background: '#1a1a2e', padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>{catInfo.icon}</span>
                  <span style={{ color: 'white', fontWeight: 800, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {catInfo.label}
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
                    {catItems.length} ítem{catItems.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div style={{ padding: '10px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                  {catItems.map(item => (
                    <ItemInput
                      key={item.id}
                      item={item}
                      valor={detalle[item.id] ?? ''}
                      onChange={v => setDetalle(prev => ({ ...prev, [item.id]: v }))}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Observación */}
          <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 14 }}>
            <div style={st.fieldLabel}>Observación (opcional)</div>
            <textarea value={observacion} onChange={e => setObs(e.target.value)}
              placeholder="Alguna nota sobre el envío..."
              rows={2}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
          </div>

          {/* Botones */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginBottom: 28 }}>
            {envioEditar ? (
              // Modo corrección
              <button onClick={handleGuardarCorreccion} disabled={guardando || totalItems === 0}
                style={{ padding: '11px 28px', border: 'none', borderRadius: 10, background: totalItems === 0 ? '#E5E7EB' : 'linear-gradient(135deg,#D97706,#B45309)', color: totalItems === 0 ? '#9CA3AF' : 'white', cursor: totalItems === 0 ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700 }}>
                {guardando ? '⏳...' : '✅ Guardar corrección'}
              </button>
            ) : (
              // Modo nuevo
              <>
                <button onClick={() => handleGuardar(false)} disabled={guardando}
                  style={{ padding: '11px 24px', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#374151' }}>
                  {guardando ? '⏳...' : '💾 Guardar borrador'}
                </button>
                <button onClick={() => handleGuardar(true)} disabled={guardando || totalItems === 0}
                  style={{ padding: '11px 28px', border: 'none', borderRadius: 10, background: totalItems === 0 ? '#E5E7EB' : 'linear-gradient(135deg,#1a1a2e,#16213e)', color: totalItems === 0 ? '#9CA3AF' : 'white', cursor: totalItems === 0 ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700 }}>
                  {guardando ? '⏳...' : '✅ Confirmar envío'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// COMPONENTE: INPUT ITEM ENVIABLE
// ─────────────────────────────────────────────
function ItemInput({ item, valor, onChange }) {
  const activo = parseFloat(valor) > 0;
  return (
    <div style={{ border: `2px solid ${activo ? '#6366F1' : '#E5E7EB'}`, borderRadius: 10, padding: '10px 12px', transition: 'border-color 0.15s' }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: '#111827', marginBottom: 4 }}>{item.nombre}</div>
      <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8 }}>{item.unidad_medida}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button onClick={() => onChange(Math.max(0, (parseFloat(valor) || 0) - 1))} style={st.countBtn}>−</button>
        <input type="number" min="0" value={valor}
          onChange={e => onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
          style={{ flex: 1, textAlign: 'center', padding: '5px', border: '2px solid #E5E7EB', borderRadius: 6, fontSize: 15, fontWeight: 800, outline: 'none', width: 0 }} />
        <button onClick={() => onChange((parseFloat(valor) || 0) + 1)} style={st.countBtn}>+</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// COMPONENTE: DETALLE DE ENVÍO
// ─────────────────────────────────────────────
function DetalleEnvio({ envio, onVolver }) {
  const porCat = {};
  (envio.detalle || []).forEach(d => {
    const cat = d.categoria || 'otros';
    if (!porCat[cat]) porCat[cat] = [];
    porCat[cat].push(d);
  });

  return (
    <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
      <div style={{ background: '#1a1a2e', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ color: 'white', fontWeight: 800, fontSize: 15 }}>
            📦 Envío #{envio.id} — {envio.bodega_nombre}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 2 }}>
            {format(new Date(envio.fecha + 'T12:00'), "d 'de' MMMM yyyy", { locale: es })} · Registrado por {envio.registrado_por_nombre}
          </div>
        </div>
        <button onClick={onVolver} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
          ← Volver
        </button>
      </div>
      <div style={{ padding: 20 }}>
        {Object.entries(porCat).map(([cat, items]) => {
          const catInfo = CAT_INFO[cat] || { label: cat, icon: '📋' };
          return (
            <div key={cat} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 8 }}>
                {catInfo.icon} {catInfo.label}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                {items.map(d => (
                  <div key={d.id} style={{ background: '#F9FAFB', borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                    <div style={{ fontWeight: 800, fontSize: 22, color: '#1a1a2e' }}>{d.cantidad}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{d.unidad_medida}</div>
                    <div style={{ fontWeight: 700, fontSize: 12, color: '#374151', marginTop: 4 }}>{d.item_nombre}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {envio.observacion && (
          <div style={{ background: '#F0FDF4', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#065F46', fontWeight: 600, marginTop: 8 }}>
            📝 {envio.observacion}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// COMPONENTE: TAB INVENTARIO PDV
// ─────────────────────────────────────────────
function TabInventarioPDV({ data, cargando }) {
  const [bodegaAbierta, setBodegaAbierta] = useState(null);

  if (cargando) {
    return <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>Cargando inventarios...</div>;
  }

  const { bodegas = [], items = [] } = data;

  return (
    <div>
      <div style={{ marginBottom: 12, fontSize: 13, color: '#6B7280', fontWeight: 600 }}>
        Último inventario enviado por cada punto de venta — usa esta información para decidir qué enviar.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
        {bodegas.map(({ bodega, ultimo_registro, detalle }) => {
          const abierta = bodegaAbierta === bodega.id;
          const itemsConStock = detalle.filter(d => {
            const val = d.cantidad;
            if (val === null || val === undefined) return false;
            if (typeof val === 'string') return val !== '' && val !== '0';
            return parseFloat(val) > 0;
          });
          const porCat = {};
          detalle.forEach(d => {
            const cat = d.categoria || 'otros';
            if (!porCat[cat]) porCat[cat] = [];
            porCat[cat].push(d);
          });

          return (
            <div key={bodega.id} style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <div style={{ background: '#1a1a2e', padding: '12px 16px', cursor: 'pointer' }} onClick={() => setBodegaAbierta(abierta ? null : bodega.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ color: 'white', fontWeight: 800, fontSize: 14 }}>🏪 {bodega.nombre}</div>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>{abierta ? '▲' : '▼'}</span>
                </div>
                {ultimo_registro ? (
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginTop: 4 }}>
                    Inventario del {format(new Date((ultimo_registro.fecha||'').slice(0,10) + 'T12:00'), "d 'de' MMM yyyy", { locale: es })}
                    {' · '}{itemsConStock.length} ítem{itemsConStock.length !== 1 ? 's' : ''} con stock
                  </div>
                ) : (
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 4 }}>Sin inventario enviado</div>
                )}
              </div>

              {abierta && (
                <div style={{ padding: '10px 14px' }}>
                  {detalle.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, padding: '12px 0' }}>Sin datos de inventario</div>
                  ) : (
                    Object.entries(porCat).map(([cat, catItems]) => {
                      const catInfo = CAT_INFO[cat] || { label: cat, icon: '📋' };
                      const conValor = catItems.filter(d => d.cantidad !== null && d.cantidad !== undefined);
                      if (!conValor.length) return null;
                      return (
                        <div key={cat} style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 5 }}>
                            {catInfo.icon} {catInfo.label}
                          </div>
                          {conValor.map(d => {
                            const esSiNo = d.unidad_medida?.toUpperCase().includes('SI/NO');
                            const stockBajo = !esSiNo && parseFloat(d.cantidad) < 10;
                            return (
                              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #F9FAFB', fontSize: 12 }}>
                                <span style={{ color: '#374151', fontWeight: 500 }}>{d.item_nombre}</span>
                                <span style={{ fontWeight: 700, color: esSiNo ? (d.cantidad === 'SI' ? '#065F46' : '#991B1B') : stockBajo ? '#DC2626' : '#1a1a2e', background: esSiNo ? (d.cantidad === 'SI' ? '#D1FAE5' : '#FEE2E2') : stockBajo ? '#FEF2F2' : 'transparent', padding: esSiNo || stockBajo ? '2px 7px' : '0', borderRadius: 6 }}>
                                  {esSiNo ? d.cantidad : `${d.cantidad} ${d.unidad_medida}`}
                                  {stockBajo && ' ⚠️'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {!abierta && itemsConStock.length > 0 && (
                <div style={{ padding: '8px 14px', display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {itemsConStock.slice(0, 4).map(d => (
                    <span key={d.id} style={{ fontSize: 11, background: '#F3F4F6', borderRadius: 6, padding: '2px 7px', color: '#374151' }}>
                      {d.item_nombre}: <strong>{d.cantidad}</strong>
                    </span>
                  ))}
                  {itemsConStock.length > 4 && (
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>+{itemsConStock.length - 4} más</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const st = {
  fieldLabel: { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 },
  select: { padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white' },
  input:  { padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none' },
  th: { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' },
  td: { padding: '10px 14px' },
  countBtn: { width: 28, height: 28, border: '2px solid #E5E7EB', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  btnVer: { padding: '4px 10px', border: '1px solid #E5E7EB', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
};
